






































































































































































































import {
    defineComponent,
    PropType,
    ref,
    computed,
    onMounted,
    watch,
    useContext,
    inject,
    Ref,
} from "@nuxtjs/composition-api";
import { useCustomOptions } from "@wemade-vsf/catalog/lib/runtime/product/composables/useCustomOptions";
import { useCart, useUser } from "@wemade-vsf/customer";
import { useWishlist } from "@wemade-vsf/wishlist";
import { useWmAttributes } from "@wemade-vsf/catalog/lib/runtime/product/composables/useWmAttributes";
import { usePriceRequest } from "@wemade-vsf/catalog/lib/runtime/product/composables/usePriceRequest";
import { useCustomerPricing } from "@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing";
import {
    formatNetRegularPrice,
    formatRegularPrice,
} from "~/stores/formatPrice";
import ProductTabs from "components/catalog/Product/ProductTabs.vue";
import { SfGallery, SfButton, SfAlert } from "@storefront-ui/vue";
import { ValidationObserver } from "vee-validate";
import ProductDetailsHeader from "components/catalog/Product/Details/ProductDetailsHeader.vue";
import ProductGallery from "components/catalog/Product/Details/ProductGallery.vue";
import ProductGalleryDesktop from "components/catalog/Product/Details/ProductGalleryDesktop.vue";
import ProductPrice from "components/catalog/Product/ProductPrice.vue";
import ProductActions from "components/catalog/Product/Details/ProductActions.vue";
import ProductUsp from "components/catalog/Product/Details/ProductUsp.vue";
import SkeletonLoader from "components/theme/SkeletonLoader.vue";
import { useProductAttachments } from "@wemade-vsf/catalog/lib/runtime/product/composables/useProductAttachments";
import type {
    Product,
    ProductAttachmentInterface,
} from "@wemade-vsf/catalog/lib/runtime/product/types";
import PartsNote from "~/modules/part-finder/components/PartsNote.vue";
import ShortDescription from "components/catalog/Product/Details/ProductDetailsDescription.vue";

export default defineComponent({
    name: "ProductDetails",
    transition: "fade",
    props: {
        product: {
            type: [Object, null] as PropType<Product>,
            default: null,
        },
        isFetching: {
            type: Boolean,
            default: true,
        },
    },
    components: {
        ValidationObserver,
        ProductDetailsHeader,
        ProductGallery,
        ProductGalleryDesktop,
        SkeletonLoader,
        SfGallery,
        ProductPrice,
        SfAlert,
        SfButton,
        ProductActions,
        ProductUsp,
        PriceRequestButton: () =>
            import("components/catalog/Product/PriceRequestButton.vue"),
        ProductTabs,
        PartsNote,
        ShortDescription,
    },
    setup(props) {
        const taxOff = inject("taxOff") as Ref<boolean>;

        const qty = ref(0);
        const {
            app: { i18n },
        } = useContext();
        const {
            addItem,
            removeItem,
            error: cartError,
            loading: isCartLoading,
            canAddToCart,
            load: loadCart,
            updateItemQty,
            cart,
        } = useCart();
        const {
            isInWishlist,
            addOrRemoveItem,
            enabled: isWishlistEnabled,
        } = useWishlist();
        const { productAttributes, deliveryTime } = useWmAttributes();
        const {
            getPriceForProduct,
            displayPricesIncludingTax,
            showCartButtonForProduct,
            showPriceForProduct,
            getNetPrice,
        } = useCustomerPricing();

        const { priceForOptions, customOptions } = useCustomOptions();
        const productPrice = ref(getPriceForProduct(props.product));
        const productPriceRegular = computed(
            () => productPrice.value?.regular ?? null
        );
        const productSpecialPrice = computed(
            () => productPrice.value?.special ?? null
        );
        const hasPriceRange = computed(
            () =>
                props.product?.__typename &&
                [
                    "ConfigurableProduct",
                    "BundleProduct",
                    "GroupedProduct",
                ].includes(props.product.__typename)
        );

        const { isQuoteProduct, showPriceOnQuoteProduct } = usePriceRequest();

        const showAddToCart = ref<boolean>(false);

        const addToCartError = computed(
            () => cartError.value?.addItem?.message
        );
        const galleryLoaded = ref(false);
        const showGalleryModal = ref(false);
        const currentGalleryIndex = ref(1);

        const openGalleryModal = (index) => {
            galleryLoaded.value = true;
            showGalleryModal.value = true;
            currentGalleryIndex.value = index;
        };

        const { isAuthenticated, user, load: loadUser } = useUser();
        function updateClientValues() {
            if (props.product) {
                productPrice.value = getPriceForProduct(props.product);
                showAddToCart.value = showCartButtonForProduct(props.product);
            }
        }

        const cartProduct = computed(() => {
            const product = ((cart.value && cart.value.items) || []).find(
                (item) => item.product.uid === props.product.uid
            );

            return product;
        });

        const productName = computed(() => {
            if (
                props.product?.configurable_product_options_selection?.variant
                    ?.name
            ) {
                return props.product.configurable_product_options_selection
                    .variant.name;
            }
            return props.product?.name || "";
        });
        const recommendedNetPrice = computed(() => {
            const priceRaw = getNetPrice(
                productPrice.value,
                props.product?.applied_tax_rate
            );
            return priceRaw;
        });

        const displayRegularPrice = computed(() => {
            let value = null;

            if (taxOff.value) {
                if (recommendedNetPrice.value?.regular) {
                    value = formatNetRegularPrice(
                        recommendedNetPrice.value?.regular
                    );
                }
            } else {
                if (productPrice.value?.regular) {
                    value = formatRegularPrice(productPrice.value?.regular);
                }
            }

            return value;
        });

        const displaySpecialPrice = computed(() => {
            const value = taxOff.value
                ? recommendedNetPrice.value?.special || null
                : productPrice.value?.special || null;
            return value;
        });

        onMounted(async () => {
            await loadCart();

            qty.value = cartProduct.value ? cartProduct.value.quantity : 0;
            if (isAuthenticated.value && !user.value) {
                await loadUser();
            }
            updateClientValues();
        });

        watch(
            () => props.product,
            (newVal) => {
                updateClientValues();
            },
            { deep: true, immediate: true }
        );
        watch(
            () => isAuthenticated.value,
            () => {
                updateClientValues();
            },
            { deep: true, immediate: true }
        );

        const uid = computed(
            () =>
                props.product?.configurable_product_options_selection?.variant
                    ?.uid ??
                props.product?.uid ??
                null
        );

        const { load: loadAttachments } = useProductAttachments();
        const productAttachments = ref<ProductAttachmentInterface[] | null>(
            null
        );

        watch(
            () => uid.value,
            async (newVal) => {
                if (uid.value) {
                    productAttachments.value = await loadAttachments(uid.value);
                }
            },
            { immediate: true }
        );

        const handleInput = async (newQuantity) => {
            if (newQuantity <= 0) {
                await removeItem({
                    product: cartProduct.value,
                });
                qty.value = 0;
                return;
            }

            if (!cartProduct.value) {
                await handleAddToCart();
                return;
            }

            await updateItemQty({
                product: cartProduct.value,
                quantity: newQuantity,
            });
            qty.value = newQuantity;
        };

        const handleAddToCart = async () => {
            await addItem({
                product: props.product,
                quantity: 1,
                customOptions: customOptions.value,
            });
            qty.value = 1;
        };

        const getProduktgruppe = (productAttributes) => {
            if (!productAttributes) {
                return null;
            }

            return productAttributes["ag_produktgruppe"] || null;
        };

        return {
            addItem,
            addItemToWishlist: addOrRemoveItem,
            hasPriceRange,
            arePricesIncludingTax: displayPricesIncludingTax,
            recommendedNetPrice,
            displayRegularPrice,
            displaySpecialPrice,
            addToCartError,
            cartError,
            canAddToCart,
            currentGalleryIndex,
            galleryLoaded,
            isCartLoading,
            isWishlistEnabled,
            isInWishlist: computed(() =>
                isInWishlist({ product: props.product })
            ),
            openGalleryModal,
            productPrice: productPriceRegular,
            productSpecialPrice,
            priceForOptions,
            customOptions,
            showGalleryModal,
            isQuoteProduct: computed(() =>
                isQuoteProduct(props.product, productAttributes.value)
            ),
            showPriceOnQuoteProduct,
            showAddToCart,
            showPrice: computed(() => showPriceForProduct(props.product)),
            qty,
            deliveryTime,
            productAttachments,
            handleInput,
            handleAddToCart,
            productName,
            taxOff,
            produktgruppe: computed(() =>
                getProduktgruppe(productAttributes.value)
            ),
        };
    },
});
